// CORE-1735 slice 8 — accessibility helpers for the new shell.
//
// react-native-web 0.21 maps only the `aria-*` props to the DOM and drops
// `accessibilityState` silently, so a toggle's checked state or a tab's selected
// state never reached screen readers on web. Components spread `a11yState(...)`
// instead: aria attributes on web, the RN prop on native.
import { WEB } from './useLiveTheme';

export interface A11yState {
  selected?: boolean;
  checked?: boolean;
  disabled?: boolean;
  busy?: boolean;
  expanded?: boolean;
  /** Toggle buttons (`aria-pressed`), never `aria-selected` on a button. */
  pressed?: boolean;
  /** The current item of a list (`aria-current`). */
  current?: boolean;
}

const ARIA: Record<keyof A11yState, string> = {
  selected: 'aria-selected',
  checked: 'aria-checked',
  disabled: 'aria-disabled',
  busy: 'aria-busy',
  expanded: 'aria-expanded',
  pressed: 'aria-pressed',
  current: 'aria-current',
};

export function a11yState(s: A11yState, web: boolean = WEB): Record<string, unknown> {
  if (!web) return { accessibilityState: s };
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(s) as (keyof A11yState)[]) {
    if (s[k] !== undefined) out[ARIA[k]] = s[k];
  }
  return out;
}

/** Slice 17 (D-121): opt a ScrollView / FlatList into the Live room's thin themed scrollbar (`[data-live-scroll]` in global.css). */
export function liveScrollProps(web: boolean = WEB): Record<string, unknown> | null {
  return web ? { dataSet: { liveScroll: 'true' } } : null;
}

/** A View that only carries a label: on web an `aria-label` is announced only with a role. */
export function labelledGroup(label: string, web: boolean = WEB): Record<string, unknown> {
  return web ? { role: 'group', 'aria-label': label } : { accessibilityLabel: label };
}
